/** Decompresses a gzip-encoded response body into a plain CSV blob, client-side, using the
 * browser's native `DecompressionStream` — shared by the V1 device-detail quick export and the
 * Export Center's async job download, both of which receive `application/gzip` from the
 * backend and never decompress server-side (see `helper/blob_storage.py`'s `download_blob_stream`
 * and `router/export/export_router.py`'s `gzip_stream`).
 *
 * Exposed as methods on `gunzipCsvUtil` rather than standalone functions: the build compiles
 * named function exports to read-only, non-configurable module bindings, which `spyOn` cannot
 * replace in tests — a plain object's properties can be spied normally. */
export const gunzipCsvUtil = {
  async gunzipToCsvBlob(blob: Blob): Promise<Blob> {
    const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
    const decompressed = await new Response(stream).blob();
    return new Blob([decompressed], { type: 'text/csv' });
  },

  triggerBlobDownload(blob: Blob, filename: string): void {
    const url = globalThis.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // NOSONAR S6441 - cleanup timeout for releasing blob URL after download initiated
    setTimeout(() => globalThis.URL.revokeObjectURL(url), 100);
  },
};

/** Fixed English granularity labels for download filenames — kept locale-independent on purpose
 *  so exported files have a stable name regardless of the user's UI language. */
const GRANULARITY_FILE_LABEL: Record<string, string> = {
  quarter_hour: '15-min',
  hour: 'Hourly',
  day: 'Daily',
  month: 'Monthly',
  year: 'Yearly',
};

/** `Export_{DD-MM-YYYY}_{granularity}_values.csv` (e.g. `Export_08-07-2026_Hourly_values.csv`).
 *  `date` is the moment of download (i.e. today), NOT the job's creation date. */
export function buildExportFileName(granularity: string, date: Date): string {
  const dd = `${date.getDate()}`.padStart(2, '0');
  const mm = `${date.getMonth() + 1}`.padStart(2, '0');
  const label = GRANULARITY_FILE_LABEL[granularity] ?? granularity;
  return `Export_${dd}-${mm}-${date.getFullYear()}_${label}_values.csv`;
}

export function parseContentDispositionFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  const match = /filename="?([^"]+)"?/.exec(contentDisposition);
  return match ? match[1] : null;
}
